import { ref ,computed} from 'vue'
import type { SetupContext } from 'vue'
import { useRequest } from '@el-plus/hooks/use-request'
import type { SelectProps, SelectOption, SelectEmits } from './select'
export function useSelect(
  props: SelectProps,
  emit: SetupContext<SelectEmits>['emit'],
) {
  let hasLoaded = false
  // 已加载的选项(api 请求结果 / props.options / 搜索结果)
  const loadedOptions = ref<SelectOption[]>([])
  const { loading, requestFn } = useRequest({
    api: props.api,
    method: props.method,
    reqData: props.reqData,
    reqParams: props.reqParams,
    reqBefore: props.reqBefore,
    reqAfter: props.reqAfter,
    cancelToken: props.remote,
    hooks: {
      success(data) {
        type OptionArr = SelectOption[]
        interface OptionObj {
          list?: OptionArr
          data?: OptionArr
        }
        if (Array.isArray(data)) {
          loadedOptions.value = data as OptionArr
        } else if (data && typeof data === 'object') {
          const rawData = data as OptionObj
          loadedOptions.value = rawData?.list || rawData?.data || []
        } else {
          loadedOptions.value = []
        }
        hasLoaded = true
      },
      fail(error) {
        console.error(error)
      },
    },
  })
  // 回显占位项:value/desc 存在且不在已加载列表中时,补充一项
  // 由 computed 自动响应 value/desc/loadedOptions 的变化,无需 watch
  const echoOption = computed<SelectOption[]>(() => {
    const v = props.modelValue
    const d = props.desc
    if ((v || v === 0) && d) {
      const exists = loadedOptions.value.some(
        (item: SelectOption) => item[props.valueKey] === v,
      )
      if (!exists) {
        return [{ [props.labelKey]: d, [props.valueKey]: v }]
      }
    }
    return []
  })
  // 最终下拉项 = 回显占位 + 已加载
  const customOptions = computed<SelectOption[]>(() => [
    ...echoOption.value,
    ...loadedOptions.value,
  ])
  // 加载下拉列表(只负责加载,不处理回显)
  const loadOptions = () => {
    if (props.api) {
      if (!props.lazy && !props.remote) {
        requestFn!()
      }
    } else if (props.options?.length) {
      loadedOptions.value = props.options
    }
  }
  // 获取选中项
  const getSelectItem = (value: SelectProps['modelValue']) => {
    if (Array.isArray(value)) {
      if (!customOptions.value) return []
      return customOptions.value.filter((item: SelectOption) => {
        return value.indexOf(item[props.valueKey]) > -1
      })
    }
    if (!customOptions.value) return {}
    return (
      customOptions.value.find((item: SelectOption) => {
        return item[props.valueKey] === value
      }) || {}
    )
  }
  const expandAttrs = computed(() => {
    return {
      loading: props.loading || loading?.value,
      remoteMethod: (query: string) => {
        if (!query) return
        requestFn!({
          $searchValue: query,
        })
      },
      onChange(value: SelectProps['modelValue']) {
        emit('change', value, getSelectItem(value))
      },
      onVisibleChange(visible: boolean) {
        if (props.api && props.lazy && visible && !hasLoaded && !props.remote) {
          requestFn!()
        }
        emit('visible-change', visible)
      },
    }
  })
  return {
    customOptions,
    loadOptions,
    expandAttrs,
  }
}
